import { useRef, useState } from "react";
import { toArabicNumerals } from "@/lib/arabicNumerals";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import type { StoreEventProps } from "@/lib/posthog";

type Product = {
  name: string;
  nameEn?: string;
  price?: string;
  imageUrl?: string;
  url?: string;
};

type ProductSamplesSliderProps = {
  products: Product[];
  fallbackUrl?: string;
  trackingProps?: StoreEventProps;
};

// Scraped product URLs can be junk truthy strings ("Not available") or truncated
// artifacts ending in "..." — treat those as missing so cards can fall back.
const isValidProductUrl = (url?: string): boolean => {
  if (!url || typeof url !== "string") return false;
  if (url.endsWith("...")) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const CARD_WIDTH = 230;
const CARD_GAP = 16;

function ChevronLeft() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function SliderButton({
  onClick,
  children,
  ariaLabel,
}: {
  onClick: () => void;
  children: React.ReactNode;
  ariaLabel: string;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: hovered ? "var(--ys-primary-soft)" : "var(--ys-paper)",
        border: hovered
          ? "1px solid var(--ys-primary)"
          : "1px solid var(--ys-rule-2)",
        boxShadow: "var(--ys-shadow-1)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: hovered ? "var(--ys-primary)" : "var(--ys-ink-2)",
        transition:
          "background 150ms var(--ys-ease), border-color 150ms var(--ys-ease), color 150ms var(--ys-ease)",
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function formatPrice(raw: string): string {
  // Extract numeric portion and convert to Arabic-Indic; keep rest as-is
  return raw.replace(/\d+(\.\d+)?/g, (match) => toArabicNumerals(match));
}

function ProductCard({
  product,
  fallbackUrl,
  trackingProps,
}: {
  product: Product;
  fallbackUrl?: string;
  trackingProps?: StoreEventProps;
}) {
  const [hovered, setHovered] = useState(false);
  const { trackStore } = usePostHogTracking();

  const hasProductUrl = isValidProductUrl(product.url);
  const href = hasProductUrl
    ? product.url
    : isValidProductUrl(fallbackUrl)
      ? fallbackUrl
      : undefined;

  const cardStyle: React.CSSProperties = {
    width: CARD_WIDTH,
    minWidth: CARD_WIDTH,
    scrollSnapAlign: "start",
    borderRadius: "var(--ys-r-md)",
    boxShadow: hovered ? "var(--ys-shadow-3)" : "var(--ys-shadow-1)",
    border: hovered
      ? "1px solid var(--ys-rule-2)"
      : "1px solid var(--ys-rule)",
    background: "var(--ys-paper)",
    transform: hovered ? "translateY(-2px)" : "translateY(0)",
    transition:
      "box-shadow 150ms var(--ys-ease), border-color 150ms var(--ys-ease), transform 150ms var(--ys-ease)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    direction: "rtl",
  };

  const inner = (
    <>
      {/* Image area */}
      <div
        style={{
          width: "100%",
          height: 160,
          background: product.imageUrl ? "transparent" : "var(--ys-bg-2)",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            loading="lazy"
          />
        ) : null}
      </div>

      {/* Body */}
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        {/* Title — 2-line clamp */}
        <p
          style={{
            fontFamily: "var(--ys-font)",
            fontWeight: 600,
            fontSize: 14,
            color: "var(--ys-ink)",
            margin: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          } as React.CSSProperties}
        >
          {product.name}
        </p>

        {/* Price */}
        {product.price && (
          <p
            style={{
              fontFamily: "var(--ys-font)",
              fontWeight: 700,
              fontSize: 16,
              color: "var(--ys-primary-hover)",
              margin: 0,
            }}
          >
            {formatPrice(product.price)} ج.م
          </p>
        )}

      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackStore.contactClicked({
            platform: "product",
            surface: "product_slider",
            product_url: href,
            ...trackingProps,
          })
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ ...cardStyle, textDecoration: "none" }}
      >
        {inner}
      </a>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={cardStyle}
    >
      {inner}
    </div>
  );
}

export default function ProductSamplesSlider({
  products,
  fallbackUrl,
  trackingProps,
}: ProductSamplesSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  if (products.length === 0) return null;

  // RTL: prev scrolls positive direction, next scrolls negative
  const scrollBy = (cards: number) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: cards * (CARD_WIDTH + CARD_GAP),
      behavior: "smooth",
    });
  };

  return (
    <div style={{ direction: "rtl" }}>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "var(--ys-font)",
              fontWeight: 700,
              fontSize: 22,
              color: "var(--ys-ink)",
              margin: 0,
            }}
          >
            نماذج من المنتجات
          </h2>
          <p
            style={{
              fontFamily: "var(--ys-font)",
              fontSize: 12,
              color: "var(--ys-ink-3)",
              margin: "4px 0 0 0",
            }}
          >
            عيّنة من موقع المتجر · للاستئناس فقط
          </p>
        </div>

        {/* Prev / Next buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          {/* Prev: RTL = scroll positive (right → left in visual, but positive in scroll) */}
          <SliderButton onClick={() => scrollBy(1)} ariaLabel="السابق">
            <ChevronRight />
          </SliderButton>
          <SliderButton onClick={() => scrollBy(-1)} ariaLabel="التالي">
            <ChevronLeft />
          </SliderButton>
        </div>
      </div>

      {/* Scroll container */}
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          gap: CARD_GAP,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          paddingBottom: 4,
        }}
      >
        {products.map((p, i) => (
          <ProductCard key={i} product={p} fallbackUrl={fallbackUrl} trackingProps={trackingProps} />
        ))}
      </div>
    </div>
  );
}
